import { useState, useCallback } from 'react';
import { createWorker, Worker } from 'tesseract.js';
import { useDropzone } from 'react-dropzone';
import { FileImage, Upload, Loader2, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

type DocumentType = 'RG' | 'CNH' | 'PASSPORT' | 'UNKNOWN';

interface ExtractedField {
  value: string;
  confidence: number;
  valid?: boolean;
}

interface OCRResult {
  documentType: DocumentType;
  confidence: number;
  extractedFields: {
    fullName?: ExtractedField;
    documentNumber?: ExtractedField;
    cpf?: ExtractedField;
    dateOfBirth?: ExtractedField;
    expirationDate?: ExtractedField;
    issuingAuthority?: ExtractedField;
  };
  rawText: string;
}

interface DocumentOCRProps {
  onComplete: (result: OCRResult, imageData: string) => void;
  onError?: (error: string) => void;
  className?: string;
}

// CPF validation algorithm (Receita Federal)
function validateCPF(cpf: string): boolean {
  const cleaned = cpf.replace(/\D/g, '');
  
  if (cleaned.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;
  
  // First digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned[i]) * (10 - i);
  }
  let digit1 = (sum * 10) % 11;
  if (digit1 === 10) digit1 = 0;
  if (digit1 !== parseInt(cleaned[9])) return false;
  
  // Second digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned[i]) * (11 - i);
  }
  let digit2 = (sum * 10) % 11;
  if (digit2 === 10) digit2 = 0;
  if (digit2 !== parseInt(cleaned[10])) return false;
  
  return true;
}

// Detect document type based on text patterns
function detectDocumentType(text: string): DocumentType {
  const upperText = text.toUpperCase();
  
  if (upperText.includes('CARTEIRA NACIONAL DE HABILITAÇÃO') || 
      upperText.includes('CNH') ||
      upperText.includes('HABILITAÇÃO') ||
      upperText.includes('CATEGORIA')) {
    return 'CNH';
  }
  
  if (upperText.includes('REGISTRO GERAL') || 
      upperText.includes('SECRETARIA') ||
      upperText.includes('IDENTIDADE')) {
    return 'RG';
  }
  
  if (upperText.includes('PASSPORT') || 
      upperText.includes('PASSAPORTE') ||
      upperText.includes('REPÚBLICA FEDERATIVA DO BRASIL')) {
    return 'PASSPORT';
  }
  
  return 'UNKNOWN';
}

// Extract fields from OCR text
function extractFields(text: string, documentType: DocumentType): OCRResult['extractedFields'] {
  const fields: OCRResult['extractedFields'] = {};
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  
  // CPF pattern: XXX.XXX.XXX-XX or XXXXXXXXXXX
  const cpfMatch = text.match(/\d{3}\.?\d{3}\.?\d{3}-?\d{2}/);
  if (cpfMatch) {
    const cpfValue = cpfMatch[0];
    fields.cpf = {
      value: cpfValue,
      confidence: 0.9,
      valid: validateCPF(cpfValue),
    };
  }
  
  // Date patterns: DD/MM/YYYY or DD-MM-YYYY
  const dateMatches = text.match(/\d{2}[\/\-]\d{2}[\/\-]\d{4}/g) || [];
  if (dateMatches.length > 0 && dateMatches[0]) {
    fields.dateOfBirth = {
      value: dateMatches[0],
      confidence: 0.8,
    };
    if (dateMatches.length > 1 && dateMatches[dateMatches.length - 1]) {
      fields.expirationDate = {
        value: dateMatches[dateMatches.length - 1],
        confidence: 0.7,
      };
    }
  }
  
  // Document number patterns
  if (documentType === 'CNH') {
    const cnhMatch = text.match(/\d{11}/);
    if (cnhMatch) {
      fields.documentNumber = {
        value: cnhMatch[0],
        confidence: 0.85,
      };
    }
  } else if (documentType === 'RG') {
    const rgMatch = text.match(/\d{1,2}\.?\d{3}\.?\d{3}-?[0-9X]/i);
    if (rgMatch) {
      fields.documentNumber = {
        value: rgMatch[0],
        confidence: 0.85,
      };
    }
  }
  
  // Name extraction (heuristic: look for lines with mostly uppercase letters)
  for (const line of lines) {
    if (line.length > 5 && 
        line.length < 60 && 
        /^[A-ZÁÉÍÓÚÂÊÎÔÛÃÕÇ\s]+$/.test(line) &&
        !line.includes('REPÚBLICA') &&
        !line.includes('SECRETARIA') &&
        !line.includes('HABILITAÇÃO')) {
      fields.fullName = {
        value: line,
        confidence: 0.75,
      };
      break;
    }
  }
  
  // Issuing authority
  const orgaoMatch = text.match(/(?:SSP|DETRAN|DIC|IFP|PC)[\/\-]?[A-Z]{2}/i);
  if (orgaoMatch) {
    fields.issuingAuthority = {
      value: orgaoMatch[0].toUpperCase(),
      confidence: 0.8,
    };
  }
  
  return fields;
}

export function DocumentOCR({
  onComplete,
  onError,
  className = '',
}: DocumentOCRProps) {
  const [imageData, setImageData] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<OCRResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const processImage = useCallback(async (image: string) => {
    setProcessing(true);
    setProgress(0);
    setError(null);

    let worker: Worker | null = null;

    try {
      worker = await createWorker('por', 1, {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setProgress(Math.round(m.progress * 100));
          }
        },
      });

      const { data } = await worker.recognize(image);
      
      const documentType = detectDocumentType(data.text);
      const extractedFields = extractFields(data.text, documentType);
      
      const ocrResult: OCRResult = {
        documentType,
        confidence: data.confidence / 100,
        extractedFields,
        rawText: data.text,
      };

      setResult(ocrResult);
      onComplete(ocrResult, image);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro ao processar documento';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      if (worker) {
        await worker.terminate();
      }
      setProcessing(false);
    }
  }, [onComplete, onError]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setImageData(dataUrl);
      processImage(dataUrl);
    };
    reader.readAsDataURL(file);
  }, [processImage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
    },
    maxFiles: 1,
    disabled: processing,
  });

  const handleRetry = () => {
    setImageData(null);
    setResult(null);
    setError(null);
    setProgress(0);
  };

  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 bg-red-50 rounded-lg ${className}`}>
        <XCircle className="w-16 h-16 text-red-400 mb-4" />
        <p className="text-red-600 text-center mb-4">{error}</p>
        <button
          onClick={handleRetry}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (processing) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 ${className}`}>
        {imageData && (
          <div className="w-48 h-32 rounded-lg overflow-hidden mb-4 border-2 border-gray-200">
            <img src={imageData} alt="Documento" className="w-full h-full object-cover" />
          </div>
        )}
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600 mb-2">Processando documento...</p>
        <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-blue-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-sm text-gray-500 mt-2">{progress}%</p>
      </div>
    );
  }

  if (result) {
    return (
      <div className={`flex flex-col ${className}`}>
        {/* Document preview */}
        {imageData && (
          <div className="mb-6">
            <div className="w-full max-w-md rounded-lg overflow-hidden border-2 border-gray-200">
              <img src={imageData} alt="Documento" className="w-full object-contain" />
            </div>
          </div>
        )}

        {/* Document type */}
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <FileImage className="w-5 h-5 text-gray-600" />
            <span className="font-medium">Tipo de Documento:</span>
            <span className={`px-2 py-1 rounded text-sm ${
              result.documentType !== 'UNKNOWN' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {result.documentType === 'UNKNOWN' ? 'Não identificado' : result.documentType}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            Confiança: {(result.confidence * 100).toFixed(0)}%
          </p>
        </div>

        {/* Extracted fields */}
        <div className="space-y-3">
          <h3 className="font-medium text-gray-700">Dados Extraídos:</h3>
          
          {result.extractedFields.fullName && (
            <FieldDisplay 
              label="Nome Completo" 
              field={result.extractedFields.fullName} 
            />
          )}
          
          {result.extractedFields.cpf && (
            <FieldDisplay 
              label="CPF" 
              field={result.extractedFields.cpf}
              showValidation
            />
          )}
          
          {result.extractedFields.documentNumber && (
            <FieldDisplay 
              label="Número do Documento" 
              field={result.extractedFields.documentNumber} 
            />
          )}
          
          {result.extractedFields.dateOfBirth && (
            <FieldDisplay 
              label="Data de Nascimento" 
              field={result.extractedFields.dateOfBirth} 
            />
          )}
          
          {result.extractedFields.expirationDate && (
            <FieldDisplay 
              label="Data de Validade" 
              field={result.extractedFields.expirationDate} 
            />
          )}
          
          {result.extractedFields.issuingAuthority && (
            <FieldDisplay 
              label="Órgão Emissor" 
              field={result.extractedFields.issuingAuthority} 
            />
          )}
        </div>

        {/* Retry button */}
        <button
          onClick={handleRetry}
          className="mt-6 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          Enviar outro documento
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        {isDragActive ? (
          <p className="text-blue-600">Solte o arquivo aqui...</p>
        ) : (
          <>
            <p className="text-gray-600 mb-2">
              Arraste uma foto do documento ou clique para selecionar
            </p>
            <p className="text-sm text-gray-500">
              Formatos aceitos: JPG, PNG, WebP
            </p>
          </>
        )}
      </div>

      <div className="mt-4 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-800 mb-2">Documentos aceitos:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• RG (Registro Geral)</li>
          <li>• CNH (Carteira Nacional de Habilitação)</li>
          <li>• Passaporte</li>
        </ul>
      </div>
    </div>
  );
}

// Helper component for displaying extracted fields
function FieldDisplay({ 
  label, 
  field, 
  showValidation = false 
}: { 
  label: string; 
  field: ExtractedField; 
  showValidation?: boolean;
}) {
  return (
    <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="font-medium">{field.value}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400">
          {(field.confidence * 100).toFixed(0)}%
        </span>
        {showValidation && field.valid !== undefined && (
          field.valid ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
          )
        )}
      </div>
    </div>
  );
}
